import React, { useEffect, useState } from "react";

import { useSelector, useDispatch } from "react-redux";
import { useUser } from "../../../authentication/authentication";

import "react-toastify/dist/ReactToastify.css";
import api from "../../../api";

import errorChecker from "../../../utils/errorChecker";
import { AnimatePresence, motion } from "framer-motion";

import {
  apiFetchError,
  apiRequest,
  apiSuccess,
} from "../../../redux/api/apiActions";
import { showAlert } from "../../../redux/alert/alertActions";
import { useNavigate } from "react-router-dom";

import { redirectWithState } from "../../../redux/navigation/navigationActions";
import SubmitLoading from "../../../components/common/SubmitLoading";
import Txt from "../../../components/Text";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import DateInput from "../../../components/form/DateInput";
import CustomSelect from "../../../components/CustomSelect";

import { usePageAccess } from "../../../utils/usePageAccess";
import { X } from "lucide-react";
import { deepCompare } from "../../../utils/deepCompare.ts";
import LoadingButton from "../../../components/common/LoadingButton.jsx";

/**
 * Text component for translations
 */
const Text = ({ children, className, page = "PATIENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

export default function EditePatient({ patient, onClose }) {
  usePageAccess("create-patient");
  const dispatch = useDispatch();
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const updatePatientKey = `updatePatient_${patient?.id || ""}`;
  const { requests } = useSelector((s) => s.api);
  const isUpdating = requests[updatePatientKey]?.loading;
  const localUser = useUser();
  const navigate = useNavigate();

  const [user, setUser] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    cin: "",
    address: "",
    medicalInsurance: "",
    allergies: "",
    chronicConditions: "",
    currentMedications: "",
    familyMedicalHistory: "",
    notes: "",
    doctor: "",
    emergencyContact: {
      name: "",
      relation: "",
      phone: "",
    },
  });
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [civilStatus, setCivilStatus] = useState("");
  const [bloodType, setBloodType] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPatientData, setOriginalPatientData] = useState(null);
  const [errors, setErrors] = useState({});

  const genderOptions = [
    { label: textTranslator("Male", "PATIENTS"), value: "Male" },
    { label: textTranslator("Female", "PATIENTS"), value: "Female" },
  ];
  const civialStatusOptions = [
    { label: textTranslator("Single", "PATIENTS"), value: "Single" },
    { label: textTranslator("Married", "PATIENTS"), value: "Married" },
    { label: textTranslator("Divorced", "PATIENTS"), value: "Divorced" },
    { label: textTranslator("Widowed", "PATIENTS"), value: "Widowed" },
    { label: textTranslator("Child", "PATIENTS"), value: "Child" },
  ];
  const bloodTypeOptions = [
    { label: "A+", value: "A+" },
    { label: "A-", value: "A-" },
    { label: "B+", value: "B+" },
    { label: "B-", value: "B-" },
    { label: "AB+", value: "AB+" },
    { label: "AB-", value: "AB-" },
    { label: "O+", value: "O+" },
    { label: "O-", value: "O-" },
  ];
  const { source, values, redirectTo } = useSelector(
    (state) => state.navigation,
  );

  // theme-aware classes
  const inputBase = darkMode
    ? "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm bg-gray-700 border border-gray-600 text-white disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed"
    : "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm border border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed ";
  const textareaBase = darkMode
    ? "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm bg-gray-700 border border-gray-600 text-white disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed"
    : "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm border border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed ";
  const labelClass = darkMode
    ? "text-xs text-gray-300"
    : "text-xs text-slate-600";
  const formCardClass = darkMode
    ? "p-4 rounded-xl border space-y-4 normal-form flex-1 bg-gray-750 border-gray-600 text-gray-100 shadow-lg backdrop-blur-sm "
    : "p-4 rounded-xl border space-y-4 normal-form flex-1 bg-white border-gray-200 text-gray-900 shadow-sm backdrop-blur-sm";

  const patientInstructions = {
    en: {
      title: "Update Patient Information",
      note: "Please review the patient's information carefully before saving. Make sure to fill in all required fields and provide accurate details to ensure the best care for the patient.",
    },
    fr: {
      title: "Mettre à jour les informations du patient",
      note: "Veuillez vérifier attentivement les informations du patient avant de sauvegarder. Assurez-vous de remplir tous les champs obligatoires et de fournir des détails précis pour garantir les meilleurs soins au patient.",
    },
    ar: {
      title: "تحديث معلومات المريض",
      note: "يرجى مراجعة معلومات المريض بعناية قبل الحفظ. تأكد من ملء جميع الحقول المطلوبة وتقديم تفاصيل دقيقة لضمان أفضل رعاية للمريض.",
    },
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName || formData.firstName.trim() === "") {
      newErrors.firstName = textTranslator("firstNameRequired", "PATIENTS");
    }

    if (!formData.lastName || formData.lastName.trim() === "") {
      newErrors.lastName = textTranslator("lastNameRequired", "PATIENTS");
    }

    if (formData.email && formData.email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = textTranslator("invalidEmail", "PATIENTS");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmergencyContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [name]: value,
      },
    }));
  };

  // Get current form data as structured object
  const getCurrentFormData = () => {
    return {
      firstName: formData.firstName || "",
      lastName: formData.lastName || "",
      phone: formData.phone || "",
      email: formData.email || "",
      cin: formData.cin || "",
      address: formData.address || "",
      medicalInsurance: formData.medicalInsurance || "",
      allergies: formData.allergies || "",
      chronicConditions: formData.chronicConditions || "",
      currentMedications: formData.currentMedications || "",
      familyMedicalHistory: formData.familyMedicalHistory || "",
      notes: formData.notes || "",
      doctor: formData.doctor || user.id || "",
      gender: gender || "",
      civilStatus: civilStatus || "",
      bloodType: bloodType || "",
      birthday: dateOfBirth || "",
      emergencyContact: {
        name: formData.emergencyContact?.name || "",
        relation: formData.emergencyContact?.relation || "",
        phone: formData.emergencyContact?.phone || "",
      },
    };
  };

  // Populate form with patient data when patient prop changes
  useEffect(() => {
    if (patient) {
      // Store original patient data for comparison
      const originalData = {
        firstName: patient.firstName || "",
        lastName: patient.lastName || "",
        phone: patient.phone || "",
        email: patient.email || "",
        cin: patient.cin || "",
        address: patient.address || "",
        medicalInsurance: patient.medicalInsurance || "",
        allergies: patient.allergies || "",
        chronicConditions: patient.chronicConditions || "",
        currentMedications: patient.currentMedications || "",
        familyMedicalHistory: patient.familyMedicalHistory || "",
        notes: patient.notes || "",
        doctor: patient.doctor || user.id || "",
        gender: patient.gender || "",
        civilStatus: patient.civilStatus || "",
        bloodType: patient.bloodType || "",
        birthday: patient.birthday || "",
        emergencyContact: {
          name: patient.emergencyContact?.name || "",
          relation: patient.emergencyContact?.relation || "",
          phone: patient.emergencyContact?.phone || "",
        },
      };

      setOriginalPatientData(originalData);

      // Set form data
      setFormData({
        firstName: patient.firstName || "",
        lastName: patient.lastName || "",
        phone: patient.phone || "",
        email: patient.email || "",
        cin: patient.cin || "",
        address: patient.address || "",
        medicalInsurance: patient.medicalInsurance || "",
        allergies: patient.allergies || "",
        chronicConditions: patient.chronicConditions || "",
        currentMedications: patient.currentMedications || "",
        familyMedicalHistory: patient.familyMedicalHistory || "",
        notes: patient.notes || "",
        doctor: patient.doctor || user.id || "",
        emergencyContact: {
          name: patient.emergencyContact?.name || "",
          relation: patient.emergencyContact?.relation || "",
          phone: patient.emergencyContact?.phone || "",
        },
      });

      // Set date of birth
      if (patient.birthday) {
        const birthdayDate = new Date(patient.birthday);
        const formattedDate = birthdayDate.toISOString().split("T")[0];
        setDateOfBirth(formattedDate);
      }

      // Set gender
      if (patient.gender) {
        setGender(patient.gender);
      }

      // Set civil status
      if (patient.civilStatus) {
        setCivilStatus(patient.civilStatus);
      }

      // Set blood type
      if (patient.bloodType) {
        setBloodType(patient.bloodType);
      }
    }
  }, [patient, user.id]);

  // Track changes using deepCompare
  useEffect(() => {
    if (originalPatientData && patient) {
      const currentData = getCurrentFormData();

      const hasDifferences = deepCompare(originalPatientData, currentData, {
        normalize: true,
        ignoreArrayOrder: false,
        ignorePaths: ["doctor"],
      });

      setHasChanges(hasDifferences);
    }
  }, [
    formData.firstName,
    formData.lastName,
    formData.phone,
    formData.email,
    formData.cin,
    formData.address,
    formData.medicalInsurance,
    formData.allergies,
    formData.chronicConditions,
    formData.currentMedications,
    formData.familyMedicalHistory,
    formData.notes,
    formData.emergencyContact.name,
    formData.emergencyContact.relation,
    formData.emergencyContact.phone,
    gender,
    civilStatus,
    bloodType,
    dateOfBirth,
    originalPatientData,
  ]);

  // compute age from birthday
  const computeAge = (dateString) => {
    if (!dateString) return "";
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : "";
  };
  const age = computeAge(dateOfBirth);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check if there are changes before submitting
    const currentData = getCurrentFormData();
    const hasDifferences = deepCompare(originalPatientData, currentData, {
      normalize: true,
      ignoreArrayOrder: false,
      ignorePaths: ["doctor"],
    });

    if (!hasDifferences) {
      dispatch(
        showAlert({
          type: "info",
          title: textTranslator("noChangesTitle", "PATIENTS"),
          message: textTranslator("noChangesMessage", "PATIENTS"),
        }),
      );
      return;
    }

    // prepare payload
    const payload = {
      ...formData,
      doctor: user.id || formData.doctor,
      birthday: dateOfBirth || new Date(),
      gender: gender || "",
      civilStatus: civilStatus || "",
      bloodType: bloodType || "",
    };

    dispatch(apiRequest("PUT", updatePatientKey));
    api
      .put(`patients/patient-id/${patient.id}`, payload)
      .then((res) => {
        dispatch(
          apiSuccess("PUT", updatePatientKey, "Patient updated successfully"),
        );
        dispatch(
          showAlert({
            type: "success",
            title: textTranslator("patientUpdatedTitle", "PATIENTS"),
            message: textTranslator("patientUpdated", "PATIENTS"),
          }),
        );

        onClose();
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.error ||
          "Erreur lors de la mise à jour des données.";
        const type = error?.response?.data?.type || null;
        errorChecker(errorMessage, type, dispatch, errorMessage);
        dispatch(apiFetchError("PUT", updatePatientKey, errorMessage));
      });
  };

  const handleCancel = () => {
    // Reset to patient data
    if (patient) {
      setFormData({
        firstName: patient.firstName || "",
        lastName: patient.lastName || "",
        phone: patient.phone || "",
        email: patient.email || "",
        cin: patient.cin || "",
        address: patient.address || "",
        medicalInsurance: patient.medicalInsurance || "",
        allergies: patient.allergies || "",
        chronicConditions: patient.chronicConditions || "",
        currentMedications: patient.currentMedications || "",
        familyMedicalHistory: patient.familyMedicalHistory || "",
        notes: patient.notes || "",
        doctor: patient.doctor || user.id || "",
        emergencyContact: {
          name: patient.emergencyContact?.name || "",
          relation: patient.emergencyContact?.relation || "",
          phone: patient.emergencyContact?.phone || "",
        },
      });

      if (patient.birthday) {
        const birthdayDate = new Date(patient.birthday);
        const formattedDate = birthdayDate.toISOString().split("T")[0];
        setDateOfBirth(formattedDate);
      }

      setGender(patient.gender || "");
      setCivilStatus(patient.civilStatus || "");
      setBloodType(patient.bloodType || "");
      setErrors({});
    }
    onClose();
  };

  const colors = {
    background: darkMode ? "bg-gray-900" : "bg-gray-50",
    card: darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
    cardHover: darkMode ? "hover:border-gray-600" : "hover:border-gray-400",
    input: darkMode
      ? "bg-gray-700 border-gray-600 placeholder-gray-500"
      : "bg-white border-gray-300 placeholder-gray-400",
    subBg: darkMode ? "bg-gray-700" : "bg-gray-100",
    text: {
      primary: darkMode ? "text-white" : "text-gray-800",
      secondary: darkMode ? "text-gray-300" : "text-gray-600",
      muted: darkMode ? "text-gray-400" : "text-gray-500",
    },
    button: {
      primary:
        "bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg hover:shadow-md",
      secondary: darkMode
        ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300",
      danger: "bg-gradient-to-br from-red-500 to-pink-600 text-white",
    },
  };

  const isFormValid = () => {
    return (
      formData.firstName?.trim() !== "" && formData.lastName?.trim() !== ""
    );
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4`}
    >
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm`}></div>
      <div
        className={`relative z-10 w-full h-full max-w-2xl mx-auto flex flex-col ${colors.card} ${formCardClass}`}
      >
        {/* Header - stays at top */}
        <div className={`px-6 py-4 border-b ${colors.subBg} flex-shrink-0`}>
          <div className="flex justify-between gap-1">
            <h2 className={`text-lg font-semibold ${colors.text.primary}`}>
              {patientInstructions[defaultLanguage]?.title ||
                patientInstructions["en"].title}
            </h2>
            <button
              onClick={handleCancel}
              className={`text-sm p-2 rounded transition-colors rounded-full ${
                darkMode
                  ? "bg-red-500 hover:bg-red-600 text-red-900"
                  : "bg-red-100 hover:bg-red-200 text-red-600"
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className={`text-sm mt-1 ${colors.text.secondary}`}>
            {patientInstructions[defaultLanguage]?.note ||
              patientInstructions["en"].note}
          </p>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <form className="h-full flex flex-col">
            <div className={arClass("grid grid-cols-1 gap-3 flex-1")}>
              {/* All your form fields here */}
              <div
                className={arClass(
                  "grid grid-cols-2 gap-2 max-sm:flex max-sm:flex-col",
                )}
              >
                <div className={arClass("flex flex-col w-full gap-2")}>
                  <label className={labelClass}>
                    <Text>First Name</Text>
                  </label>
                  <input
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={arClass(inputBase)}
                    name="firstName"
                    placeholder={textTranslator("enterFirstName", "PATIENTS")}
                    disabled={isUpdating}
                  />
                </div>
                <div className={arClass("flex flex-col w-full gap-2")}>
                  <label className={labelClass}>
                    <Text>Last Name</Text>
                  </label>
                  <input
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={arClass(inputBase)}
                    name="lastName"
                    placeholder={textTranslator("enterLastName", "PATIENTS")}
                    disabled={isUpdating}
                  />
                </div>
                {(errors.firstName || errors.lastName) && (
                  <div className="text-red-600 text-xs my-1 flex flex-col gap-2 col-span-2">
                    {errors.firstName && (
                      <span className={arClass("text-xs !text-red-400")}>
                        *{errors.firstName}
                      </span>
                    )}
                    {errors.lastName && (
                      <span className={arClass("text-xs !text-red-400")}>
                        *{errors.lastName}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div
                className={arClass(
                  "grid grid-cols-2 gap-2 max-sm:flex max-sm:flex-col",
                )}
              >
                <div className={arClass("flex flex-col gap-2")}>
                  <label className="text-xs text-slate-600">
                    <Text>Date of Birth</Text>
                  </label>
                  <DateInput
                    className={inputBase}
                    onChange={(e) => {
                      setDateOfBirth(e.target.value);
                    }}
                    name="birthday"
                    value={dateOfBirth}
                    disabled={isUpdating}
                  />
                </div>
                <div className={arClass("flex flex-col gap-2 relative group")}>
                  <label className="text-xs text-slate-600">
                    <Text>Age</Text>
                  </label>
                  <input
                    className={`${inputBase} !cursor-not-allowed`}
                    name="age"
                    value={age || ""}
                    readOnly
                    disabled
                  />

                  <AnimatePresence>
                    <motion.span
                      className={`absolute left-0 top-full mt-1 hidden group-hover:block text-xs px-2 py-1 rounded z-[10000] ${
                        darkMode
                          ? "text-white bg-slate-700"
                          : "text-white bg-slate-700"
                      }`}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      exit={{ opacity: 0, y: -5 }}
                    >
                      <Text>Age is calculated based on the date of birth provided.No need to enter it manually.</Text>
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>

              <div
                className={arClass(
                  "grid grid-cols-2 gap-2 max-sm:flex max-sm:flex-col items-center",
                )}
              >
                <div className={arClass("flex flex-col w-full gap-2")}>
                  <label className="text-xs text-slate-600">
                    <Text>CIN</Text>
                  </label>
                  <input
                    value={formData.cin}
                    onChange={handleInputChange}
                    className={arClass(inputBase)}
                    name="cin"
                    placeholder={textTranslator("enterCIN", "PATIENTS")}
                    disabled={isUpdating}
                  />
                </div>
                <div className={arClass("flex flex-col w-full gap-2")}>
                  <label className="text-xs text-slate-600">
                    <Text>Gender</Text>
                  </label>
                  <CustomSelect
                    options={genderOptions}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className={arClass("w-full")}
                    isMobile={window.innerWidth < 640}
                    allowCreate={false}
                    allowSearch={false}
                    disabled={isUpdating}
                  />
                </div>
                {gender === "" && (
                  <div className="text-red-600 text-xs my-1 flex flex-col gap-2 col-span-3">
                    <span className={arClass("text-xs !text-red-400")}>
                      *<Text>genderRequired</Text>
                    </span>
                    {errors.cin && (
                      <span className={arClass("text-xs !text-red-400")}>
                        *{errors.cin}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <label className="text-xs text-slate-600">
                <Text>Phone</Text>
              </label>
              <input
                value={formData.phone}
                onChange={handleInputChange}
                className={arClass(`${inputBase} flex-1`)}
                name="phone"
                placeholder={textTranslator("enterPhoneNumber", "PATIENTS")}
                disabled={isUpdating}
              />
              {errors.phone && (
                <span className="text-red-600 text-xs">{errors.phone}</span>
              )}

              <label className="text-xs text-slate-600">
                <Text>Email</Text>
              </label>
              <input
                value={formData.email}
                onChange={handleInputChange}
                className={arClass(inputBase)}
                name="email"
                placeholder={textTranslator("enterEmailAddress", "PATIENTS")}
                disabled={isUpdating}
              />
              {errors.email && (
                <span className="text-red-600 text-xs">{errors.email}</span>
              )}

              <label className="text-xs text-slate-600">
                <Text>Home Address</Text>
              </label>
              <textarea
                value={formData.address}
                onChange={handleInputChange}
                className={arClass(inputBase)}
                name="address"
                placeholder={textTranslator("enterHomeAddress", "PATIENTS")}
                rows={3}
                disabled={isUpdating}
              />

              <div
                className={arClass(
                  "grid grid-cols-2 gap-3 max-sm:flex max-sm:flex-col",
                )}
              >
                <div className={arClass("flex flex-col w-full gap-2")}>
                  <label className="text-xs text-slate-600">
                    <Text>Civil Status</Text>
                  </label>
                  <CustomSelect
                    options={civialStatusOptions}
                    value={civilStatus}
                    onChange={(e) => setCivilStatus(e.target.value)}
                    className={arClass("w-full")}
                    isMobile={window.innerWidth < 640}
                    allowCreate={false}
                    allowSearch={false}
                  />
                </div>
                <div className={arClass("flex flex-col w-full gap-2")}>
                  <label className="text-xs text-slate-600">
                    <Text>Blood Type</Text>
                  </label>
                  <CustomSelect
                    options={bloodTypeOptions}
                    value={bloodType}
                    onChange={(e) => setBloodType(e.target.value)}
                    className={arClass("w-full")}
                    isMobile={window.innerWidth < 640}
                    allowCreate={false}
                    allowSearch={false}
                  />
                </div>
                {civilStatus === "" && (
                  <div className="text-red-600 text-xs my-1 flex flex-col gap-2 col-span-3">
                    <span className={arClass("text-xs !text-red-400")}>
                      *<Text>civilStatusRequired</Text>
                    </span>
                  </div>
                )}
              </div>

              <label className="text-xs text-slate-600">
                <Text>Allergies</Text>
              </label>
              <input
                value={formData.allergies}
                onChange={handleInputChange}
                className={arClass(inputBase)}
                name="allergies"
                placeholder={textTranslator("enterAllergies", "PATIENTS")}
                disabled={isUpdating}
              />

              <label className="text-xs text-slate-600">
                <Text>Chronic Conditions (comma separated)</Text>
              </label>
              <input
                value={formData.chronicConditions}
                onChange={handleInputChange}
                className={arClass(inputBase)}
                name="chronicConditions"
                placeholder={textTranslator(
                  "enterChronicConditions",
                  "PATIENTS",
                )}
                disabled={isUpdating}
              />

              <label className="text-xs text-slate-600">
                <Text>Current Medications (comma separated)</Text>
              </label>
              <input
                value={formData.currentMedications}
                onChange={handleInputChange}
                className={arClass(inputBase)}
                name="currentMedications"
                placeholder={textTranslator(
                  "enterCurrentMedications",
                  "PATIENTS",
                )}
                disabled={isUpdating}
              />

              <label className="text-xs text-slate-600">
                <Text>Family Medical History</Text>
              </label>
              <input
                value={formData.familyMedicalHistory}
                onChange={handleInputChange}
                className={arClass(inputBase)}
                name="familyMedicalHistory"
                placeholder={textTranslator(
                  "Enter family medical history",
                  "PATIENTS",
                )}
                disabled={isUpdating}
              />

              <label className="text-xs text-slate-600">
                <Text>Insurance Provider</Text>
              </label>
              <input
                value={formData.medicalInsurance}
                onChange={handleInputChange}
                className={arClass(inputBase)}
                name="medicalInsurance"
                placeholder={textTranslator(
                  "enterInsuranceProvider",
                  "PATIENTS",
                )}
                disabled={isUpdating}
              />

              <label className="text-xs text-slate-600">
                <Text>Notes</Text>
              </label>
              <textarea
                value={formData.notes}
                onChange={handleInputChange}
                className={arClass(textareaBase)}
                name="notes"
                placeholder={textTranslator("enterNotes", "PATIENTS")}
                rows={3}
                disabled={isUpdating}
              />

              {/* Emergency Contact Section */}
              <div className="pt-2 border-t">
                <h4 className="text-sm font-semibold">
                  <Text>Emergency Contact</Text>
                </h4>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  <label className="text-xs text-slate-600">
                    <Text>Name</Text>
                  </label>
                  <input
                    value={formData.emergencyContact.name}
                    onChange={handleEmergencyContactChange}
                    className={arClass(inputBase)}
                    name="name"
                    placeholder={textTranslator(
                      "enterEmergencyContactName",
                      "PATIENTS",
                    )}
                    disabled={isUpdating}
                  />

                  <label className="text-xs text-slate-600">
                    <Text>Relation</Text>
                  </label>
                  <input
                    value={formData.emergencyContact.relation}
                    onChange={handleEmergencyContactChange}
                    className={arClass(inputBase)}
                    name="relation"
                    placeholder={textTranslator(
                      "enterEmergencyContactRelationship",
                      "PATIENTS",
                    )}
                    disabled={isUpdating}
                  />

                  <label className="text-xs text-slate-600">
                    <Text>Contact Phone</Text>
                  </label>
                  <input
                    value={formData.emergencyContact.phone}
                    onChange={handleEmergencyContactChange}
                    className={arClass(inputBase)}
                    name="phone"
                    placeholder={textTranslator("contactPhone", "PATIENTS")}
                    disabled={isUpdating}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        {/* Sticky Footer with Buttons */}
        <div
          className={arClass(
            `sticky bottom-0 bg-inherit border-t mt-4 flex-shrink-0  ${colors.subBg} flex-shrink-0`,
          )}
        >
          <div className="flex gap-2 justify-end p-2">
            <button
              type="button"
              className={`${
                darkMode
                  ? "px-3 py-2 bg-gray-700 text-white rounded text-sm max-sm:text-xs"
                  : "px-3 py-2 bg-slate-100 rounded text-sm"
              }`}
              onClick={handleCancel}
            >
              <Text>Cancel</Text>
            </button>

            <LoadingButton
              loading={isUpdating}
              loadingText={textTranslator("Updating...", "PATIENTS")}
              
              variant="primary"
              disabled={!isFormValid() || !hasChanges}
              type="submit"
              onClick={handleSubmit}
              title={
                !isFormValid()
                  ? textTranslator("fillFormCorrectly", "PATIENTS")
                  : isUpdating
                    ? textTranslator("Updating...", "PATIENTS")
                    : !hasChanges
                      ? textTranslator("noChangesToUpdate", "PATIENTS")
                      : textTranslator("Update Patient", "PATIENTS")
              }
            >
              <Text>Update Patient</Text>
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
}
